/**
 * 通用工具函数
 */

/**
 * 延迟执行
 * @param ms 延迟时间（毫秒）
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * 延迟执行（别名）
 * @param ms 延迟时间（毫秒）
 */
export const delay = (ms: number = 300): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * 防抖函数
 * @param func 要防抖的函数
 * @param wait 等待时间
 * @param immediate 是否立即执行
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };

    const callNow = immediate && !timeout;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
};

/**
 * 节流函数
 * @param func 要节流的函数
 * @param limit 时间限制
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * 深度克隆
 * @param obj 要克隆的对象
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
};

/**
 * 对象深度合并
 * @param target 目标对象
 * @param sources 源对象
 */
export const deepMerge = <T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key] as any, source[key] as any);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return deepMerge(target, ...sources);
};

/**
 * 判断是否为对象
 * @param item 要判断的项
 */
const isObject = (item: any): item is Record<string, any> => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * 生成唯一ID
 * @param length ID长度
 */
export const generateId = (length: number = 8): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * 生成UUID
 */
export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * 获取数据类型
 * @param value 要检测的值
 */
export const getType = (value: any): string => {
  return Object.prototype.toString.call(value).slice(8, -1).toLowerCase();
};

/**
 * 判断是否为空值
 * @param value 要判断的值
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * 数组去重
 * @param arr 要去重的数组
 * @param key 对象数组去重的键名
 */
export const uniqueArray = <T>(arr: T[], key?: keyof T): T[] => {
  if (!key) {
    return [...new Set(arr)];
  }
  const seen = new Set();
  return arr.filter(item => {
    const value = item[key];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

/**
 * 数组分组
 * @param arr 要分组的数组
 * @param key 分组键名或函数
 */
export const groupBy = <T>(
  arr: T[],
  key: keyof T | ((item: T) => string | number)
): Record<string, T[]> => {
  return arr.reduce((groups, item) => {
    const groupKey = typeof key === 'function' ? key(item) : item[key];
    const groupName = String(groupKey);
    
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(item);
    
    return groups;
  }, {} as Record<string, T[]>);
};

/**
 * 树形数据扁平化
 * @param tree 树形数据
 * @param childrenKey 子节点键名
 */
export const flattenTree = <T extends Record<string, any>>(
  tree: T[],
  childrenKey: string = 'children'
): T[] => {
  const result: T[] = [];
  
  const flatten = (nodes: T[]) => {
    nodes.forEach(node => {
      result.push(node);
      if (node[childrenKey] && Array.isArray(node[childrenKey])) {
        flatten(node[childrenKey]);
      }
    });
  };
  
  flatten(tree);
  return result;
};

/**
 * 扁平数据转树形结构
 * @param list 扁平数据
 * @param idKey ID键名
 * @param parentIdKey 父ID键名
 * @param childrenKey 子节点键名
 */
export const listToTree = <T extends Record<string, any>>(
  list: T[],
  idKey: string = 'id',
  parentIdKey: string = 'parentId',
  childrenKey: string = 'children'
): T[] => {
  if (!list || list.length === 0) {
    return [];
  }

  const map = new Map<any, T & { [key: string]: any }>();
  const roots: (T & { [key: string]: any })[] = [];

  // 创建映射
  list.forEach(item => {
    map.set(item[idKey], { ...item, [childrenKey]: [] });
  });

  // 构建树形结构
  list.forEach(item => {
    const node = map.get(item[idKey]);
    if (!node) return;

    const parentId = item[parentIdKey];

    // 父节点为 0、null、undefined 或者找不到父节点时，作为根节点
    if (!parentId || parentId === 0 || !map.has(parentId)) {
      roots.push(node);
    } else {
      const parent = map.get(parentId);
      if (parent) {
        parent[childrenKey].push(node);
      }
    }
  });

  return roots;
};

/**
 * 查找树节点
 * @param tree 树形数据
 * @param predicate 查找条件
 * @param childrenKey 子节点键名
 */
export const findTreeNode = <T extends Record<string, any>>(
  tree: T[],
  predicate: (node: T) => boolean,
  childrenKey: string = 'children'
): T | null => {
  for (const node of tree) {
    if (predicate(node)) {
      return node;
    }
    if (node[childrenKey] && Array.isArray(node[childrenKey])) {
      const found = findTreeNode(node[childrenKey], predicate, childrenKey);
      if (found) return found;
    }
  }
  return null;
};

/**
 * 获取树节点路径
 * @param tree 树形数据
 * @param targetId 目标节点ID
 * @param idKey ID键名
 * @param childrenKey 子节点键名
 */
export const getTreePath = <T extends Record<string, any>>(
  tree: T[],
  targetId: any,
  idKey: string = 'id',
  childrenKey: string = 'children'
): T[] => {
  const path: T[] = [];
  
  const findPath = (nodes: T[]): boolean => {
    for (const node of nodes) {
      path.push(node);
      
      if (node[idKey] === targetId) {
        return true;
      }
      
      if (node[childrenKey] && Array.isArray(node[childrenKey])) {
        if (findPath(node[childrenKey])) {
          return true;
        }
      }
      
      path.pop();
    }
    return false;
  };
  
  findPath(tree);
  return path;
};

/**
 * 过滤树节点
 * @param tree 树形数据
 * @param predicate 过滤条件
 * @param childrenKey 子节点键名
 */
export const filterTree = <T extends Record<string, any>>(
  tree: T[],
  predicate: (node: T) => boolean,
  childrenKey: string = 'children'
): T[] => {
  return tree.reduce((acc, node) => {
    const newNode = { ...node } as T;

    if (node[childrenKey] && Array.isArray(node[childrenKey])) {
      (newNode as any)[childrenKey] = filterTree(node[childrenKey], predicate, childrenKey);
    }

    if (predicate(newNode) || ((newNode as any)[childrenKey] && (newNode as any)[childrenKey].length > 0)) {
      acc.push(newNode);
    }

    return acc;
  }, [] as T[]);
};

/**
 * 字符串首字母大写
 * @param str 字符串
 */
export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * 驼峰转下划线
 * @param str 驼峰字符串
 */
export const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
};

/**
 * 下划线转驼峰
 * @param str 下划线字符串
 */
export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * 获取URL参数
 * @param url URL地址
 */
export const getUrlParams = (url: string = window.location.href): Record<string, string> => {
  const params: Record<string, string> = {};
  const urlObject = new URL(url);
  
  urlObject.searchParams.forEach((value, key) => {
    params[key] = value;
  });
  
  return params;
};

/**
 * 获取单个查询参数值
 * @param name 参数名
 * @param defaultValue 默认值
 */
export const getQueryParam = (name: string, defaultValue?: string): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name) || defaultValue || '';
};

/**
 * 设置URL参数
 * @param params 参数对象
 * @param url 基础URL
 */
export const setUrlParams = (params: Record<string, any>, url: string = window.location.href): string => {
  const urlObject = new URL(url);
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      urlObject.searchParams.set(key, String(value));
    } else {
      urlObject.searchParams.delete(key);
    }
  });
  
  return urlObject.toString();
};

/**
 * 复制文本到剪贴板
 * @param text 要复制的文本
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // 兼容旧浏览器
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    }
  } catch (error) {
    console.error('复制失败:', error);
    return false;
  }
};

/**
 * 下载文件
 * @param content 文件内容
 * @param filename 文件名
 * @param type 文件类型
 */
export const downloadFile = (content: string | Blob, filename: string, type?: string): void => {
  const blob = content instanceof Blob ? content : new Blob([content], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * 获取文件扩展名
 * @param filename 文件名
 */
export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
};

/**
 * 获取文件大小描述
 * @param bytes 字节数
 */
export const getFileSizeDescription = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
